import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { MyAreaTitle } from "../../components";
import Button from "../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../components/button/types";
import { CvEditorModal } from "./CvEditorModal";
import { CvEditorSession } from "./types/cv-editor.types";
import {
  getCurrentSession,
  initiateSession,
} from "../../../../../../services/cv-editor.service";
import { SessionHistory } from "./components/SessionHistory";

export function CVEditorPage() {
  const { t } = useTranslation("cvEditor");
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  // This state will hold the session object that we pass to the modal
  const [sessionForModal, setSessionForModal] =
    useState<CvEditorSession | null>(null);

  // 1. Fetch the current session on page load to determine the button text.
  const { data: currentSession, isLoading: isLoadingSession } = useQuery(
    "currentCvSession",
    getCurrentSession,
    {
      refetchOnWindowFocus: false,
    }
  );

  // 2. Mutation for initiating a new session if one doesn't exist.
  const initiateSessionMutation = useMutation(initiateSession, {
    onSuccess: (newSession) => {
      // When a new session is created, set it as the one for the modal and open it.
      setSessionForModal(newSession);
      setIsModalOpen(true);
    },
    onError: (error) => {
      console.error("Failed to initiate session:", error);
      // TODO: Show an error toast to the user
    },
  });

  // --- EVENT HANDLERS ---

  const handleOpenModal = () => {
    if (currentSession) {
      // If a session already exists, we'll use it for the modal.
      setSessionForModal(currentSession);
      setIsModalOpen(true);
    } else {
      // If no session exists, trigger the mutation to create one.
      // The `onSuccess` callback will then open the modal.
      initiateSessionMutation.mutate();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSessionForModal(null); // Clear the session from state when modal closes
    // Refetch data to ensure the button text and session history are up-to-date.
    queryClient.invalidateQueries("currentCvSession");
    queryClient.invalidateQueries("cvSessionHistory");
  };

  // Determine the button text based on whether a session exists.
  const buttonText = currentSession
    ? t("page.continueButton")
    : t("page.startButton");
  const isLoading = isLoadingSession || initiateSessionMutation.isLoading;

  return (
    <>
      {/* Page Content */}
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("page.title")} />
      </div>
      <div className="mb-6">
        <p>{t("page.description")}</p>
      </div>
      <div className="flex justify-end">
        <Button
          className="mb-2"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={handleOpenModal}
          disabled={isLoading}
        >
          {isLoading ? t("page.loadingButton") : buttonText}
        </Button>
      </div>

      <div className="my-6 border-t border-gray-200"></div>
      <SessionHistory />

      {/* The Modal Component */}
      {/* The modal is only rendered when it's open and has a session to work with */}
      {isModalOpen && sessionForModal && (
        <CvEditorModal
          session={sessionForModal}
          isOpen={isModalOpen}
          onClose={handleModalClose}
        />
      )}
    </>
  );
}
